import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';
import { useMemo } from 'react';

import { cart_registration_started } from '@/analytics';
import {
  CartItemType,
  QuantityMode,
} from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { usePermissions } from '@/hooks/usePermissions';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { OrderSummaryList } from '@/imports/ui/components/Cart/OrderSummary/OrderSummaryList';
import { CartItemTypeEnum } from '@/imports/ui/components/Cart/TypeDefs';
import { useCart } from '@/state/cart/useCart';
import { shouldUseMemberLearningRegistrationCopy } from '@/utilities/memberLearningRegistrationCopy';

import { EventHeader } from './components/EventHeader';
import { OrderModalLayout } from './components/OrderModalLayout';
import { OrderSummaryHeader } from './components/OrderSummaryHeader';
import { SignInWarning } from './components/SignInWarning';
import { TicketSelect } from './components/TicketSelect';
import { TotalAmount } from './components/TotalAmount';
import { useEventRegistrations } from './hooks/useEventRegistrations';
import { useTicketEligibility } from './hooks/useTicketEligibility';
import { useUserGroups } from './hooks/useUserGroups';
import { useAddToCartModalData } from './hooks/useAddToCartModalData';
import { ProceedToRegistrationButton } from './components/ProceedToRegistrationButton';

interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  isConference: boolean;
}

export const AddToCartModal: React.FC<AddToCartModalProps> = (props) => {
  const router = useRouter();
  const permissions = usePermissions();
  const { items: cartItems, addToCart } = useCart();
  const { user } = useCurrent();
  const ticketLinkId = router.query.ticketLinkId
    ? router.query.ticketLinkId.toString()
    : undefined;

  const [isAddingToCart, setIsAddingToCart] = React.useState(false);

  // Use the custom hook for data fetching
  const { event, canSignUp, loading, error } = useAddToCartModalData({
    eventId: props.eventId,
    isOpen: props.isOpen,
  });

  const useEnrollmentCopy = useMemo(
    () => (event ? shouldUseMemberLearningRegistrationCopy(event) : false),
    [event]
  );

  // Check eligibility for tickets
  const hasMembershipInCart = cartItems.some(
    (item) => item.type === CartItemTypeEnum.MEMBERSHIP
  );
  const { userGroupIds } = useUserGroups(user);
  const { registrationsData } = useEventRegistrations(props.eventId, user);
  const eligibility = useTicketEligibility(
    event,
    user,
    hasMembershipInCart,
    userGroupIds,
    registrationsData
  );

  // Get current cart items for this event
  const eventCartItems = useMemo(() => {
    return cartItems.filter(
      (item) =>
        (item.type === CartItemTypeEnum.EVENTS ||
          item.type === CartItemTypeEnum.COURSE) &&
        item.eventId === props.eventId
    );
  }, [cartItems, props.eventId]);
  
  //total amount of items in the cart
  const totalAmount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.total || 0), 0),
    [cartItems]
  );

  // Check if any cart item for this event is not eligible
  // This ensures the "Proceed" button is disabled when attendee doesn't meet event visibility requirements
  const hasIneligibleTicket = React.useMemo(() => {
    if (!event || eventCartItems.length === 0) return false;

    return eventCartItems.some((cartItem) => {
      // Find the corresponding ticket in the event data
      const ticket = event.ticketsCurrentUserCanPurchase?.find(
        (t: any) => t._id === cartItem.eventTicketId
      );

      if (!ticket) return false;

      const eligibilityCheck = eligibility.getEligibilityForTicket(ticket);
      return !eligibilityCheck.isEligible;
    });
  }, [eventCartItems, event, eligibility]);

  // Check if any cart item is a magic link ticket missing a valid ticketLinkId
  // Magic link tickets require a ticketLinkId in the URL to proceed
  const hasInvalidMagicLinkTicket = React.useMemo(() => {
    if (!event || eventCartItems.length === 0) return false;

    return eventCartItems.some((cartItem) => {
      // Find the corresponding ticket in the event data
      const ticket = event.ticketsCurrentUserCanPurchase?.find(
        (t: any) => t._id === cartItem.eventTicketId
      );

      if (!ticket) return false;

      // Check if this is a magic link ticket without a valid ticketLinkId
      const isMagicLinkTicket = ticket.canPurchase?.type === 'MAGIC_LINK';
      return isMagicLinkTicket && !ticketLinkId;
    });
  }, [eventCartItems, event, ticketLinkId]);

  // Combined validation: disable proceed if there are eligibility issues or invalid magic link tickets
  const shouldDisableProceed = hasInvalidMagicLinkTicket || hasIneligibleTicket;

  const postLoginRedirectUrl = React.useMemo(() => {
    const parsed = queryString.parseUrl(router.asPath);
    const nextQuery = { ...parsed.query, rModalOpen: 'true' };
    const queryStringified = queryString.stringify(nextQuery);
    const fullUrl = queryStringified ? `${parsed.url}?${queryStringified}` : parsed.url;
    
    // Don't double-encode the URL - Next.js will handle encoding when needed
    return fullUrl;
  }, [router.asPath]);

  // Auto-select magic link ticket when user is authenticated and has ticketLinkId
  React.useEffect(() => {
    if (
      !permissions.isAuthenticated ||
      !event?.ticketsCurrentUserCanPurchase ||
      eventCartItems.length > 0 ||
      !ticketLinkId
    ) {
      return;
    }

    const magicLinkTicket = event.ticketsCurrentUserCanPurchase.find(
      (ticket: any) => ticket.canPurchase?.type === 'MAGIC_LINK'
    );

    if (!magicLinkTicket) {
      return;
    }

    // Auto-add magic link ticket to cart
    const autoAddMagicLinkTicket = async () => {
      try {
        await addToCart([
          {
            itemId: magicLinkTicket._id,
            itemType: CartItemType.EVENTS,
            eventId: props.eventId,
            quantity: 1,
            eventTicketId: magicLinkTicket._id,
            quantityMode: QuantityMode.SET,
          },
        ]);
      } catch (error) {
        console.error('Failed to auto-add magic link ticket:', error);
      }
    };

    // Add a small delay to ensure the modal is fully rendered and permissions are stable
    const timeoutId = setTimeout(() => {
      autoAddMagicLinkTicket();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    permissions.isAuthenticated,
    event?.ticketsCurrentUserCanPurchase,
    eventCartItems.length,
    ticketLinkId,
    props.eventId,
    addToCart,
  ]);

  const handleAddToCart = async () => {
    if (eventCartItems.length === 0) return;

    setIsAddingToCart(true);

    try {
      props.onClose();

      // Navigate to the first ticket's registration page
      const firstTicketId = eventCartItems[0].eventTicketId;
      cart_registration_started({
        entity_id: props.eventId,
        entity_type: 'event',
        event_id: props.eventId,
        event_name: props.eventName,
        event_type: props.isConference ? 'conference' : 'simple',
        ticket_id: firstTicketId,
        cart_item_count: eventCartItems.length,
        cart_total_amount: totalAmount,
        payment_item_type: 'ticket',
        page_type: 'event_registration_modal',
        page_section: 'events',
      });
      const ticketQuery = ticketLinkId
        ? `?${queryString.stringify({ ticketLinkId })}`
        : '';
      router.push(
        `/events/${props.eventId}/attendee-registration/${firstTicketId}${ticketQuery}`
      );
    } catch (error) {
      console.error('Failed to navigate to registration:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const isSelectionDisabled = !permissions.isAuthenticated || isAddingToCart;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" minH="400px">
        <ModalHeader borderBottom="1px" borderBottomColor="#F5F5F5" p="0">
          {/* Header content is now handled by the grid layout */}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p="0" maxHeight="80vh" overflow="hidden">
          {loading ? (
            <Loading />
          ) : error || !event ? (
            <UnexpectedError />
          ) : (
            <OrderModalLayout
              headerLeft={
                <EventHeader
                  eventName={props.eventName}
                  eventStartDate={props.eventStartDate}
                  eventEndDate={props.eventEndDate}
                />
              }
              headerRight={<OrderSummaryHeader />}
              tickets={
                <TicketSelect
                  event={event}
                  eventId={props.eventId}
                  isDisabled={isSelectionDisabled}
                />
              }
              orderList={
                <Box display="flex" flexDirection="column" height="100%">
                  {/* Scrollable list */}
                  <Box flex="1" overflowY="auto">
                    <OrderSummaryList
                      showDetailedBreakdown={true}
                      itemSpacing={2}
                      fontSize="sm"
                      isScrollable={false}
                      maxHeight="100%"
                    />
                  </Box>

                  {/* Sticky total */}
                  {eventCartItems.length > 0 && (
                    <Box
                      position="sticky"
                      bottom="0"
                      bg="gray.50"
                      pt="4"
                      mt="2"
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      <Flex justify="flex-end">
                        <TotalAmount total={totalAmount} />
                      </Flex>
                    </Box>
                  )}
                </Box>
              }
            />
          )}
        </ModalBody>
        <ModalFooter
          p="6"
          borderTop="1px"
          borderTopColor="gray.200"
          display="block"
        >
          {/* ProceedToRegistrationButton */}
          <ProceedToRegistrationButton
            isAuthenticated={permissions.isAuthenticated}
            signInUrl={`/login?redirect=${encodeURIComponent(postLoginRedirectUrl)}`}
            onProceed={handleAddToCart}
            isLoading={isAddingToCart}
            isDisabled={
              eventCartItems.length === 0 || loading || shouldDisableProceed
            }
            useEnrollmentCopy={useEnrollmentCopy}
          />

          {!permissions.isAuthenticated && (
            <Box mt="4">
              <SignInWarning canSignUp={canSignUp} />
            </Box>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
